"use client";

import { useContext } from "react";

import {
  getCategoryLinkQueryFromFilterOptions,
  getDefaultUrlType,
} from "@helpers/plasmic/catalog-entries";

import HorizontalCarouselWrapper from "@components/swagmagic/dynamic/horizontal-carousel-wrapper";
import { CatalogEntriesProps } from "@components/swagmagic/dynamic/slices/catalog-entries";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import DetailedInfoCard from "@components/swagmagic/dynamic/cards/category/detailed-info-card";
import InfoCard from "@components/swagmagic/dynamic/cards/category/info-card";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { MobileScrollWrapper } from "@components/swagmagic/dynamic/mobile-scroll-wrapper";

import {
  CE_01,
  CE_02,
  CE_06,
  CE_14,
  DETAILED_CATEGORY_CARD_VARIANT,
  getCarouselSlidesPerRow,
  getHeaderButtonLinkDataAndType,
} from "@helpers/plasmic/slices";

import styles from "./main.module.scss";

export interface CatalogEntriesCarouselProps extends CatalogEntriesProps {
  categoriesToRender: any[];
}

const CatalogEntriesCarousel = (props: CatalogEntriesCarouselProps) => {
  const {
    className,
    variant,
    title,
    description,
    btnText,
    btnLinkType,
    btnLinkOtherUrl,
    btnLinkCategorySlug,
    linkToNewTab,
    categoriesToRender,
    categorySlug,
    linkRouteDefaults,
  } = props;

  const { isDesktopDevice: isDesktop } = useContext(CurrentDeviceContext);

  const isDetailedCategoryCard = DETAILED_CATEGORY_CARD_VARIANT.includes(variant);

  const defaultSlidesPerRow = getCarouselSlidesPerRow(variant);

  const linkDataAndType = getHeaderButtonLinkDataAndType({
    linkType: btnLinkType,
    otherUrl: btnLinkOtherUrl,
    categorySlug: btnLinkCategorySlug || categorySlug,
  });

  const renderCardLink = (category: any) => {
    const {
      name,
      description: categoryDescription,
      url,
      portraitImage,
      imageMobile,
      imageTablet,
      urlType,
      filterOptions,
      otherUrl,
      linkToNewTab: categoryLinkToNewTab,
      btnText: categoryBtnText,
      code,
    } = category;
    const imageUrl = portraitImage?.url || portraitImage;
    const imageMobileUrl = imageMobile?.url || imageMobile;
    const imageTabletUrl = imageTablet?.url || imageTablet;
    const cardComponent = isDetailedCategoryCard ? (
      <DetailedInfoCard
        variant={variant}
        src={imageUrl}
        title={name}
        description={categoryDescription}
        url={url}
        linkToNewTab={categoryLinkToNewTab ?? false}
        btnText={categoryBtnText}
      />
    ) : (
      <InfoCard
        variant={variant}
        src={imageUrl}
        srcMobile={imageMobileUrl}
        srcTablet={imageTabletUrl}
        title={name}
      />
    );

    const cardLinkDataAndType = getHeaderButtonLinkDataAndType({
      linkType: urlType || getDefaultUrlType(categorySlug),
      otherUrl: otherUrl,
      categorySlug: code,
    });

    return (
      <LinkWithLang
        {...linkRouteDefaults}
        {...cardLinkDataAndType}
        query={getCategoryLinkQueryFromFilterOptions(filterOptions)}
        target={categoryLinkToNewTab ? "_blank" : "_self"}
        key={code}
      >
        {cardComponent}
      </LinkWithLang>
    );
  };

  return (
    <HorizontalCarouselWrapper
      isGridCarousel={isDetailedCategoryCard}
      className={`${styles.horizontalCarouselWrapper} ${className} ${
        variant === CE_01 || variant === CE_02 || variant === CE_06
          ? styles.multipleRowCarousel
          : ""
      }
        ${variant === CE_06 ? styles.smallImagesCarousel : ""}
      `}
      title={title}
      description={description}
      btnText={btnText}
      linkToNewTab={linkToNewTab}
      slidesPerRow={defaultSlidesPerRow}
      linkDataAndType={linkDataAndType}
      linkRouteDefaults={linkRouteDefaults}
      showWithCarousel={isDetailedCategoryCard || isDesktop}
      descriptionTextClass={variant === CE_14 ? styles.descriptionCE14 : undefined}
      carouselContainerClassName={variant === CE_14 ? styles.ce14Arrows : undefined}
    >
      {isDetailedCategoryCard || isDesktop ? (
        categoriesToRender?.map(renderCardLink)
      ) : (
        <MobileScrollWrapper
          containerClassName={
            variant === CE_14 ? styles.mobileScrollContainerCE14 : styles.mobileScrollContainer
          }
          scrollTrackClassName={styles.scrollTrack}
          scrollThumbClassName={styles.scrollThumb}
          depKey={categoriesToRender?.length}
        >
          {categoriesToRender?.map((category) => (
            <div
              key={category.code}
              className={
                variant === CE_14 ? styles.mobileCardWrapperSmall : styles.mobileCardWrapper
              }
            >
              {renderCardLink(category)}
            </div>
          ))}
        </MobileScrollWrapper>
      )}
    </HorizontalCarouselWrapper>
  );
};

export default CatalogEntriesCarousel;
